import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useFeatureFlagEnabled } from "posthog-js/react"
import AdHocCommands from "@/api/adHocCommands"
import { type GboxTimelineFeed } from "@/lib/gboxTimeline"
import GboxTimelineParser from "@/lib/gboxTimelineParser"
import { readNavigatorTimelineFailedAccounts } from "@/lib/navigatorTimeline"
import NavigatorTimelineParser from "@/lib/navigatorTimelineParser"
import PlatformScienceTimelineParser from "@/lib/platformScienceTimelineParser"

type ProviderId = "gbox" | "navigator" | "platformScience"

// Polish uses genitive forms, so they drop straight into "przystanków z …".
const PROVIDER_LABEL_KEYS = {
  gbox: "planner.feeds.providers.gbox",
  navigator: "planner.feeds.providers.navigator",
  platformScience: "planner.feeds.providers.platformScience",
} as const

// Which providers failed, or "all" when the whole load threw. Kept as data so
// the message is worded at render time, in the current interface language.
type FeedsFailure = ProviderId[] | "all" | null

type ProviderLoad = {
  id: ProviderId
  // `degraded` covers the half-failure a rejected promise cannot express: the
  // IQ succeeded, but the provider served less than the whole fleet.
  load: () => Promise<{ feeds: GboxTimelineFeed[]; degraded: boolean }>
}

// Same provider fan-out as TimelineView, reused by the planner view.
export const useTimelineFeeds = (bookmarkedDriverChatJids: string[]) => {
  const { t } = useTranslation()
  const isGboxEnabled = Boolean(useFeatureFlagEnabled("gbox-timeline-view"))
  const isNavigatorEnabled = Boolean(
    useFeatureFlagEnabled("navigator-timeline-view"),
  )
  const isPlatformScienceEnabled = Boolean(
    useFeatureFlagEnabled("platform-science-timeline-view"),
  )
  const isAnyProviderEnabled =
    isGboxEnabled || isNavigatorEnabled || isPlatformScienceEnabled

  const [feeds, setFeeds] = useState<GboxTimelineFeed[]>([])
  const [loading, setLoading] = useState(true)
  const [failure, setFailure] = useState<FeedsFailure>(null)
  const [retryCount, setRetryCount] = useState(0)
  const retry = () => setRetryCount((count) => count + 1)

  useEffect(() => {
    let isCancelled = false

    const load = async () => {
      if (
        !isAnyProviderEnabled ||
        !window.XMPP ||
        bookmarkedDriverChatJids.length === 0
      ) {
        setFeeds([])
        setFailure(null)
        setLoading(false)
        return
      }

      setLoading(true)
      setFailure(null)
      const client = window.XMPP
      try {
        const providers: ProviderLoad[] = []
        if (isGboxEnabled) {
          providers.push({
            id: "gbox",
            load: async () => ({
              feeds: GboxTimelineParser.parse(
                await AdHocCommands.getGboxTimelineView(
                  client,
                  bookmarkedDriverChatJids,
                ),
              ),
              degraded: false,
            }),
          })
        }
        if (isNavigatorEnabled) {
          providers.push({
            id: "navigator",
            load: async () => {
              const response =
                await AdHocCommands.getNavigatorTimelineView(client)
              return {
                feeds: NavigatorTimelineParser.parse(response),
                degraded: readNavigatorTimelineFailedAccounts(response) > 0,
              }
            },
          })
        }
        if (isPlatformScienceEnabled) {
          providers.push({
            id: "platformScience",
            load: async () => ({
              feeds: PlatformScienceTimelineParser.parse(
                await AdHocCommands.getPlatformScienceTimelineView(
                  client,
                  bookmarkedDriverChatJids,
                ),
              ),
              degraded: false,
            }),
          })
        }

        const results = await Promise.allSettled(
          providers.map((provider) => provider.load()),
        )
        if (isCancelled) return

        const loadedFeeds: GboxTimelineFeed[] = []
        const failedProviders: ProviderId[] = []
        results.forEach((result, index) => {
          const { id } = providers[index]
          if (result.status === "rejected") {
            console.warn(`Failed to load ${id} timeline`, result.reason)
            failedProviders.push(id)
            return
          }
          loadedFeeds.push(...result.value.feeds)
          // A provider that answered but skipped part of the fleet is still a
          // failure worth naming, even when the others filled the view.
          if (result.value.degraded) failedProviders.push(id)
        })

        setFeeds(loadedFeeds)
        setFailure(failedProviders.length > 0 ? failedProviders : null)
      } catch (loadError) {
        console.warn("Failed to load timeline feeds", loadError)
        if (isCancelled) return
        setFeeds([])
        setFailure("all")
      } finally {
        if (!isCancelled) {
          setLoading(false)
        }
      }
    }

    void load()
    return () => {
      isCancelled = true
    }
  }, [
    isAnyProviderEnabled,
    isGboxEnabled,
    isNavigatorEnabled,
    isPlatformScienceEnabled,
    bookmarkedDriverChatJids,
    retryCount,
  ])

  const error = useMemo(() => {
    if (failure === null) return null
    if (failure === "all") return t("planner.feeds.loadFailed")
    return t("planner.feeds.loadFailedFrom", {
      providers: failure.map((id) => t(PROVIDER_LABEL_KEYS[id])).join(", "),
    })
  }, [failure, t])

  return { feeds, loading, error, isAnyProviderEnabled, retry }
}
